import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Send, X, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'tgmart_chatbot_session';

const HIDDEN_PREFIXES = ['/admin', '/vendor/dashboard', '/vendor/messages', '/vendor/login'];

const initialGreeting = {
  id: 'greet',
  role: 'assistant',
  content:
    "Hi! I'm TelanganaMart's assistant. Ask me about categories, vendors, or policies — or type 'post requirement' to reach vendors directly.",
};

const msgId = () => `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const ChatWidget = () => {
  const location = useLocation();
  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([initialGreeting]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || null; } catch { return null; }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { id: msgId(), role: 'user', content: text }]);
    setInput('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const data = await resp.json();
      if (data?.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(SESSION_KEY, data.session_id); } catch (e) {
          // localStorage may be unavailable (private mode) — ignore silently.
          void e;
        }
      }
      setMessages((m) => [...m, { id: msgId(), role: 'assistant', content: data?.reply || 'Sorry, no response.' }]);
    } catch (err) {
      setMessages((m) => [...m, {
        id: msgId(),
        role: 'assistant',
        content: 'Sorry — I ran into a network hiccup. Please retry in a moment.',
      }]);
      console.warn('[chatbot] send failed', err);
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId]);

  if (hidden) return null;

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chatbot-launcher"
          className="fixed z-[60] bottom-20 md:bottom-6 right-4 md:right-6 h-14 w-14 rounded-full bg-gradient-to-br from-[#ee5e23] to-[#d24e15] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-transform flex items-center justify-center"
          aria-label="Open TelanganaMart chatbot"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed z-[70] bottom-20 md:bottom-6 right-4 md:right-6 w-[calc(100vw-2rem)] sm:w-96 h-[75vh] max-h-[600px] rounded-2xl bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#ee5e23] to-[#d24e15] text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <div>
                <div className="font-semibold text-sm">TelanganaMart Assistant</div>
                <div className="text-[10px] opacity-90">AI-powered · replies in seconds</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chatbot-close"
              className="p-1 rounded hover:bg-white/20 transition-colors"
              aria-label="Close chatbot"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50 dark:bg-[#0b1220]"
          >
            {messages.map((m, i) => (
              <div
                key={m.id || `m-${i}`}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#2c54b2] text-white rounded-br-sm'
                      : 'bg-white dark:bg-[#1f2937] text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl bg-white dark:bg-[#1f2937] border border-gray-200 dark:border-gray-700 text-gray-500 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-gray-200 dark:border-gray-700 p-2 bg-white dark:bg-[#111827]"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-full bg-white dark:bg-[#0b1220] focus:outline-none focus:ring-2 focus:ring-[#ee5e23]/50"
              data-testid="chatbot-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="h-9 w-9 flex items-center justify-center rounded-full bg-[#ee5e23] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#d24e15] transition-colors"
              data-testid="chatbot-send"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
